import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/ui/LanguageToggle';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary-600 p-2">
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt="KernLogic"
                  className="size-6 text-white"
                />
              </div>
              <span className="text-xl font-bold text-white">KernLogic</span>
            </Link>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/80 text-slate-300 hover:border-slate-500 hover:bg-slate-700/80 hover:text-white"
              >
                <Link to="/request-organization">
                  <ArrowLeft className="mr-2 size-4" />
                  {t('privacy.navigation.back')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-slate-700/50 bg-slate-800/90 p-8 shadow-xl backdrop-blur-sm">
            <h1 className="mb-4 text-3xl font-bold text-white">{t('privacy.title')}</h1>

            <p className="mb-8 text-sm text-slate-400">{t('privacy.effectiveDate')}</p>

            <div className="max-w-none space-y-6">
              <p className="text-slate-200">{t('privacy.introduction')}</p>

              {/* Render all sections dynamically */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(sectionNum => (
                <div key={sectionNum} className="space-y-4">
                  <h2 className="text-xl font-semibold text-white">
                    {t(`privacy.sections.${sectionNum}.title`)}
                  </h2>
                  <p className="text-slate-200">{t(`privacy.sections.${sectionNum}.content`)}</p>

                  {/* Special handling for sections with subsections */}
                  {sectionNum === 3 && (
                    <div className="ml-4 space-y-3">
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.3.1.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.3.1.content')}</p>
                        <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                          {(() => {
                            const items = t('privacy.sections.3.1.items', {
                              returnObjects: true,
                            });
                            const itemArray = Array.isArray(items) ? items : [];
                            return itemArray.map((item, index) => <li key={index}>{item}</li>);
                          })()}
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.3.2.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.3.2.content')}</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.3.3.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.3.3.content')}</p>
                      </div>
                    </div>
                  )}

                  {/* Special handling for section 5 with uses list */}
                  {sectionNum === 5 && (
                    <ul className="ml-6 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const uses = t('privacy.sections.5.uses', {
                          returnObjects: true,
                        });
                        const useArray = Array.isArray(uses) ? uses : [];
                        return useArray.map((use, index) => <li key={index}>{use}</li>);
                      })()}
                    </ul>
                  )}

                  {/* Special handling for section 6 with subsections */}
                  {sectionNum === 6 && (
                    <div className="ml-4 space-y-3">
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.6.1.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.6.1.content')}</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.6.2.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.6.2.content')}</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {t('privacy.sections.6.3.title')}
                        </h3>
                        <p className="text-slate-200">{t('privacy.sections.6.3.content')}</p>
                      </div>
                    </div>
                  )}

                  {/* Special handling for section 10 with rights list */}
                  {sectionNum === 10 && (
                    <ul className="ml-6 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const rights = t('privacy.sections.10.rights', {
                          returnObjects: true,
                        });
                        const rightsArray = Array.isArray(rights) ? rights : [];
                        return rightsArray.map((right, index) => <li key={index}>{right}</li>);
                      })()}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
